import React, { forwardRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ParagraphSmallText } from '../../Texts';
import ExclamationIcon from '../../../assets/icons/exclamation.svg?react';

export const TextInputContainer = styled.div`
  background-color: ${({ theme }) => theme.background.light};
  color: ${({ theme }) => theme.text.regular};
  border: 1px solid transparent;
  width: 100%;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  padding-left: 15px;
  position: relative;
  padding-right: 15px;
  gap: 15px;
  box-sizing: border-box;

  ${({ $isFocused, $hasError, theme }) => {
    if ($hasError) {
      return `
        border-color: ${theme.text.error};
        box-shadow: 0 0 0 0.5px ${theme.text.error};
      `;
    }
    if ($isFocused) {
      return `
        border-color: ${theme.primary.primary};
        box-shadow: 0 0 0 0.5px ${theme.primary.primary};
      `;
    }
    return '';
  }}
`;

export const StyledInput = styled.input`
  border: unset;
  height: 100%;
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  width: 100%;
  color: ${({ theme }) => theme.text.regular};
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: normal;
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

export const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.text.secondary};
`;

export const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.text.error};
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
  min-height: 16px;
  line-height: 16px;
`;

const CopyContainer = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CopiedContainer = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0px;
  right: 0px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const CopiedItem = styled.div`
  background-color: ${({ theme }) => theme.background.light};
  padding: 10px 30px;
  border-radius: 999px;
`;

const TooltipContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: help;
`;

const TooltipIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: 100%;
  }
`;

const TooltipText = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background-color: ${({ theme }) => theme.background.primary};
  color: ${({ theme }) => theme.text.regular};
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const TextInput = forwardRef((props, ref) => {
  const {
    label,
    placeholder,
    value,
    icon,
    type = 'text',
    onChange,
    onBlur,
    onFocus,
    error,
    copy,
    tooltip,
    ...rest
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const theme = useTheme();

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };
  const [copied, setCopied] = useState(false);

  const copyHandler = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ width: '100%', padding: '0 1px', boxSizing: 'border-box' }}>
      {label && <Label>{label}</Label>}
      <TextInputContainer $isFocused={isFocused} $hasError={!!error}>
        {icon && icon}
        <StyledInput
          ref={ref}
          placeholder={placeholder}
          type={type}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...rest}
        />
        {copy && (
          <CopyContainer onClick={copyHandler}>
            <img src='/copy.webp' width={16} height={16} />
          </CopyContainer>
        )}
        {tooltip && (
          <TooltipContainer
            onMouseEnter={() => setIsTooltipVisible(true)}
            onMouseLeave={() => setIsTooltipVisible(false)}
          >
            <TooltipIcon style={{ color: theme.text.regular }}>
              <ExclamationIcon width={16} height={16} />
            </TooltipIcon>
            <TooltipText $isVisible={isTooltipVisible}>{tooltip}</TooltipText>
          </TooltipContainer>
        )}
        {copied && (
          <CopiedContainer>
            <CopiedItem>
              <ParagraphSmallText>Link copied</ParagraphSmallText>
            </CopiedItem>
          </CopiedContainer>
        )}
      </TextInputContainer>
      {!!error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';

export default TextInput;
